import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import {
  type PricingPlanQueryV4Row,
  useGetV4PricingPlanReportByPlan,
} from "../api/pricing_plan/v4PricingPlanReportByPlan";
import { css } from "../../../../styled-system/css";
import { useServerTable, type AppColumns } from "../../../components/table/tableFeatures";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
} from "../../../components/table/usePersistedTableState";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { resetTable } from "../../../components/table/utils";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { Button } from "../../../components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import { ENROLL_OPTIONS } from "../../advertising/filters/productLifecycleFilters";
import PricingPlanCard from "../../pricingv2/PricingPlanCard";
import { useGetV4PricingPlanReportOptions } from "../api/pricing_plan/v4PricingPlanReportOptions";
import { TableErrorWrapper } from "../../../components/table/TableErrorWrapper";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useTagFilterOptions } from "../../tags/api/useTagFilterOptions";

const PricingPlanReportTable = ({
  columns,
  state,
  initialState,
  setters,
}: {
  columns: AppColumns<PricingPlanQueryV4Row>;
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
}) => {
  const { t } = useTranslation(["perf_reports", "common"]);
  const debounceSearch = useDebounce(state.globalFilter, 500);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const byPlanOptionsGetter = useGetV4PricingPlanReportByPlan();
  const pricingPlanByPlanReportQuery = useBlockWindowQuery(byPlanOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debounceSearch,
  });
  const productReportOptionsQuery = useQuery({
    ...useGetV4PricingPlanReportOptions(),
    enabled: !tagsEnhancedEnabled,
  });
  const tagEnhancedOptions = useTagFilterOptions("PRICING_PLAN", { enabled: tagsEnhancedEnabled });

  const categoriesQuery = useCategoriesQuery();

  const tagOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.perf_reports["All Tags"]) },
      ...(productReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [productReportOptionsQuery.data?.data.tags, t],
  );

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.perf_reports["All Categories"]) },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data, t],
  );
  const table = useServerTable({
    data: pricingPlanByPlanReportQuery.data?.data,
    columns,
    alternateSortingEnabled: true,
    getRowCanExpand: () => true,
    getRowId: (row) => row.pricing_plan_id.toString(),
    enableRowSelection: true,
    rowCount: pricingPlanByPlanReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    meta: {
      showSkeleton:
        pricingPlanByPlanReportQuery.isFetching &&
        (!pricingPlanByPlanReportQuery.data || pricingPlanByPlanReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        category_id: categoryOptions,
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        pricing_plan_status: ENROLL_OPTIONS,
      },
    },
  });

  // const selected = selectionStateToSelectedIds(table.state.rowSelection);

  return (
    <div>
      <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"}>
        {/* <BulkActionButton selected={selected} options={[]} /> */}
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <TableErrorWrapper
        error={pricingPlanByPlanReportQuery.error}
        userMessage={t(($) => $.perf_reports["Error loading pricing plan report."])}
      >
        <Table
          table={table}
          className={css({ minWidth: "100%" })}
          renderExpander={(row) => <PricingPlanCard planId={row.original.pricing_plan_id} hasBorder={false} />}
        />
      </TableErrorWrapper>
      {/* <HStack width={"100%"} padding={"small"}>
        <DownloadV4Report downloadMutation={exportMutation} />
      </HStack> */}
    </div>
  );
};

export default PricingPlanReportTable;
